import { isEuMemberState } from '@mahawi/eshop-common/dist/src/legal/eu-member-states';
import {
  type EEconomicOperatorValidationError,
  EResponsiblePersonRole,
  type ICountry,
  type IManufacturer,
  type IManufacturerInput,
  type IResponsiblePerson,
} from '@mahawi/eshop-common/src/types';
import { Alert, Button, Col, Form, Input, Row, Select, Space } from 'antd';
import { type Rule } from 'antd/es/form';
import React, { useEffect } from 'react';

import { getCountryOptions } from '../../fragments/get-country-options';
import { SPACING } from '../../theme/tokens';

export interface IOperatorFormValues {
  uuid?: string;
  name: string;
  street: string;
  city: string;
  zip: string;
  countryIsoCode2: string;
  email: string | null;
  website: string | null;
  role?: EResponsiblePersonRole;
}

export const toOperatorFormValues = (
  operator: IManufacturer | IResponsiblePerson,
): IOperatorFormValues => ({
  uuid: operator.uuid,
  name: operator.name,
  street: operator.street,
  city: operator.city,
  zip: operator.zip,
  countryIsoCode2: operator.countryType.isoCode2,
  email: operator.email,
  website: operator.website,
  role: 'role' in operator ? operator.role : undefined,
});

export const toOperatorInput = (
  values: IOperatorFormValues,
  countryType: ICountry,
): IManufacturerInput => ({
  uuid: values.uuid,
  name: values.name,
  street: values.street,
  city: values.city,
  zip: values.zip,
  countryType,
  email: values.email?.trim() ? values.email.trim() : null,
  website: values.website?.trim() ? values.website.trim() : null,
});

const ROLE_OPTIONS = Object.values(EResponsiblePersonRole).map(
  (role: EResponsiblePersonRole) => ({ label: role, value: role }),
);

function OperatorForm({
  initialValues,
  countries,
  withRole,
  inProcess,
  validationErrors,
  onSubmit,
  onReset,
}: {
  initialValues: IOperatorFormValues | null;
  countries: ICountry[] | undefined;
  withRole: boolean;
  inProcess: boolean;
  validationErrors: EEconomicOperatorValidationError[];
  onSubmit: (values: IOperatorFormValues, countryType: ICountry) => void;
  onReset: () => void;
}): React.ReactElement {
  const [form] = Form.useForm<IOperatorFormValues>();

  useEffect((): void => {
    form.resetFields();

    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [form, initialValues]);

  const contactRule: Rule = ({ getFieldValue }) => ({
    validator(): Promise<void> {
      return getFieldValue('email') || getFieldValue('website')
        ? Promise.resolve()
        : Promise.reject(new Error('Enter an email or a website.'));
    },
  });

  const onFinish = (values: IOperatorFormValues): void => {
    const countryType: ICountry | undefined = countries?.find(
      (country: ICountry): boolean =>
        country.isoCode2 === values.countryIsoCode2,
    );

    if (countryType) {
      onSubmit(values, countryType);
    }
  };

  const countryRules: Rule[] = [
    { required: true },
    ...(withRole
      ? [
          {
            validator(_rule: Rule, value: string | undefined): Promise<void> {
              return !value || isEuMemberState(value)
                ? Promise.resolve()
                : Promise.reject(
                    new Error('The responsible person must be in the EU.'),
                  );
            },
          },
        ]
      : []),
  ];

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item name="uuid" hidden>
        <Input />
      </Form.Item>
      <Row gutter={SPACING.lg}>
        <Col span={8}>
          <Form.Item
            name="name"
            label="Name or trademark"
            rules={[{ required: true }]}
          >
            <Input disabled={inProcess} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="street" label="Street" rules={[{ required: true }]}>
            <Input disabled={inProcess} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="zip" label="ZIP" rules={[{ required: true }]}>
            <Input disabled={inProcess} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="city" label="City" rules={[{ required: true }]}>
            <Input disabled={inProcess} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={SPACING.lg}>
        <Col span={4}>
          <Form.Item
            name="countryIsoCode2"
            label="Country"
            rules={countryRules}
          >
            <Select
              showSearch={{ optionFilterProp: 'label' }}
              disabled={inProcess}
              options={getCountryOptions(countries, 'isoCode2')}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email' }, contactRule]}
            dependencies={['website']}
          >
            <Input disabled={inProcess} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="website"
            label="Website"
            rules={[
              { pattern: /^https:\/\//, message: 'Use an https:// address.' },
              contactRule,
            ]}
            dependencies={['email']}
          >
            <Input disabled={inProcess} />
          </Form.Item>
        </Col>
        {withRole ? (
          <Col span={4}>
            <Form.Item name="role" label="Role" rules={[{ required: true }]}>
              <Select options={ROLE_OPTIONS} disabled={inProcess} />
            </Form.Item>
          </Col>
        ) : null}
      </Row>

      {validationErrors.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          title="Not saved"
          description={validationErrors.join(', ')}
          style={{ marginBottom: SPACING.lg }}
        />
      ) : null}

      <Space size={SPACING.lg} align="start">
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={inProcess}
        >
          Save
        </Button>
        <Button onClick={onReset} disabled={inProcess}>
          New
        </Button>
      </Space>
    </Form>
  );
}

export default OperatorForm;
